import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { AbstractVisual } from "../components/AbstractVisual";
import { api } from "../lib/api";

export default function Article() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setItem(null); setMissing(false);
    api.get("news", slug).then(setItem).catch(() => setMissing(true));
  }, [slug]);

  const paragraphs = useMemo(() => (item?.body || "").split("\n").filter(Boolean), [item]);

  if (missing) return (
    <main className="pt-48 pb-40 text-center">
      <p className="font-serif text-3xl mb-6">Article not found.</p>
      <Link to="/insights" className="text-sm text-zinc-400 hover:text-white">← Back to Insights</Link>
    </main>
  );
  if (!item) return <main className="pt-48 pb-40 text-center text-zinc-500 font-mono text-sm">Loading…</main>;

  return (
    <main className="pb-28">
      <Seo title={item.title} path={`/insights/${slug}`} description={item.excerpt} />
      <div className="relative h-[46vh] overflow-hidden">
        <AbstractVisual hue={item.hue ?? 222} seed={slug.length * 17} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030305] to-transparent" />
      </div>
      <article className="max-w-3xl mx-auto px-6 -mt-24 relative" data-testid="article-body">
        <Reveal>
          <Link to="/insights" data-testid="back-to-insights" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors mb-8">
            <ArrowLeft size={14} /> Insights
          </Link>
          <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-5">{item.category} · {item.date}</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight mb-10">{item.title}</h1>
          <div className="space-y-6">
            {paragraphs.map((p, i) => (
              <p key={p.slice(0, 60)} className={`leading-relaxed font-light ${i === 0 ? "text-xl text-zinc-200" : "text-base text-zinc-400"}`}>{p}</p>
            ))}
          </div>
        </Reveal>
      </article>
    </main>
  );
}
